import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { embedUrl } from "@/lib/cinema";
import { ArrowLeft, CalendarPlus, Clock, Film, Lock, MapPin, Sparkles, Star, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import WatchPartyDialog from "@/components/WatchPartyDialog";
import CopyInviteButton from "@/components/CopyInviteButton";

function formatPartyTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export default function CinemaDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parties, setParties] = useState([]);
  const [partyOpen, setPartyOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: ps }] = await Promise.all([
        api.get(`/cinema/movies/${id}`),
        api.get(`/cinema/movies/${id}/watch-parties`),
      ]);
      setMovie(m);
      setParties(ps || []);
      if (!m.locked) {
        api.post(`/cinema/movies/${id}/view`).catch(() => {});
      }
    } catch {
      toast.error("Could not load this title");
      navigate("/cinema");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleRsvp = async (eventId) => {
    try {
      const { data } = await api.post(`/events/${eventId}/rsvp`);
      setParties((prev) => prev.map((p) => (p.id === eventId ? { ...p, ...data } : p)));
    } catch {
      toast.error("Could not update RSVP");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${movie.title}" from Faith Cinema?`)) return;
    try {
      await api.delete(`/cinema/movies/${movie.id}`);
      toast.success("Removed from library");
      navigate("/cinema");
    } catch {
      toast.error("Could not remove");
    }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-[var(--jj-muted)]">Loading…</div>;
  if (!movie) return null;

  const embed = embedUrl({ source_type: movie.source_type, source_id: movie.source_id });
  const isAdmin = user?.role === "admin";
  const upcomingParties = parties.filter((p) => {
    if (!p.starts_at) return false;
    const t = new Date(p.starts_at).getTime();
    return !isNaN(t) && t > Date.now() - 60 * 60 * 1000;
  });

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <Link to="/cinema" className="inline-flex items-center gap-1.5 text-sm text-jjblue/80 hover:text-burgundy" data-testid="cinema-back">
          <ArrowLeft className="h-4 w-4" /> Back to Faith Cinema
        </Link>

        <div className="mt-6 grid lg:grid-cols-[1.6fr_1fr] gap-8">
          {/* Player */}
          <div>
            <div className="aspect-video rounded-2xl overflow-hidden border border-jj bg-jjblue shadow-xl" data-testid="cinema-player">
              {movie.locked ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-cream p-8 text-center">
                  <Lock className="h-10 w-10 text-gold" strokeWidth={1.4} />
                  <h3 className="font-serif text-3xl mt-4">A premium-tier title</h3>
                  <p className="mt-3 text-cream/80 max-w-md">Become a <span className="text-gold">Believer</span> or <span className="text-gold">Disciple</span> member to unlock this film and every premium addition.</p>
                  <Link to="/pricing" className="btn-gold mt-6 !py-2.5 !px-5 text-sm">View memberships</Link>
                </div>
              ) : embed ? (
                <iframe
                  title={movie.title}
                  src={embed}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-cream/70">
                  <Film className="h-12 w-12" strokeWidth={1.2} />
                </div>
              )}
            </div>

            <div className="mt-6">
              {movie.featured && (
                <span className="inline-flex items-center gap-1 text-[11px] tracking-wide bg-gold/20 text-burgundy px-2 py-1 rounded-full uppercase font-semibold">
                  <Star className="h-3 w-3 fill-current" /> Featured
                </span>
              )}
              <h1 className="font-serif text-4xl sm:text-5xl text-jjblue mt-3 leading-tight" data-testid="cinema-title">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-[var(--jj-muted)]">
                {movie.year && <span>{movie.year}</span>}
                {movie.duration_min && (<><span>·</span><span>{movie.duration_min} min</span></>)}
                {movie.rating && (<><span>·</span><span className="px-1.5 py-0.5 border border-jj rounded text-xs">{movie.rating}</span></>)}
                {movie.director && (<><span>·</span><span>Directed by {movie.director}</span></>)}
                <span>·</span>
                <span className="capitalize">{movie.category}</span>
              </div>
              {movie.description && (
                <p className="mt-5 text-jjblue/85 leading-relaxed whitespace-pre-wrap">{movie.description}</p>
              )}
              {movie.tags?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {movie.tags.map((t) => (
                    <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-cream-surface border border-jj text-jjblue/80">#{t}</span>
                  ))}
                </div>
              )}

              {!movie.locked && (
                <div className="mt-7 jj-card p-5 flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-cream-surface to-cream">
                  <div className="flex items-start gap-3 max-w-md">
                    <div className="h-10 w-10 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                      <Users className="h-5 w-5 text-burgundy" strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="font-serif text-lg text-jjblue leading-tight">Don&rsquo;t watch alone</div>
                      <p className="mt-0.5 text-sm text-[var(--jj-text-secondary)]">Schedule a community watch party — we&rsquo;ll post it to the Events board.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <CopyInviteButton
                      url={`${window.location.origin}/api/og/cinema/${movie.id}${user?.id ? `?inviter=${user.id}` : ""}`}
                      label="Share title"
                      copiedLabel="Link copied"
                      testId="cinema-share-title"
                    />
                    <button
                      onClick={() => setPartyOpen(true)}
                      className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2"
                      data-testid="host-watch-party-btn"
                    >
                      <CalendarPlus className="h-4 w-4" /> Host a watch party
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="jj-card p-6">
              <div className="overline text-burgundy flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> About this title
              </div>
              <dl className="mt-4 grid grid-cols-3 gap-y-3 text-sm">
                <dt className="col-span-1 text-[var(--jj-muted)]">Category</dt>
                <dd className="col-span-2 text-jjblue capitalize">{movie.category}</dd>
                <dt className="col-span-1 text-[var(--jj-muted)]">Language</dt>
                <dd className="col-span-2 text-jjblue">{movie.language || "English"}</dd>
                {movie.year && (<><dt className="col-span-1 text-[var(--jj-muted)]">Year</dt><dd className="col-span-2 text-jjblue">{movie.year}</dd></>)}
                {movie.duration_min && (<><dt className="col-span-1 text-[var(--jj-muted)]">Runtime</dt><dd className="col-span-2 text-jjblue">{movie.duration_min} min</dd></>)}
                {movie.director && (<><dt className="col-span-1 text-[var(--jj-muted)]">Director</dt><dd className="col-span-2 text-jjblue">{movie.director}</dd></>)}
                {movie.rating && (<><dt className="col-span-1 text-[var(--jj-muted)]">Rated</dt><dd className="col-span-2 text-jjblue">{movie.rating}</dd></>)}
                <dt className="col-span-1 text-[var(--jj-muted)]">Source</dt>
                <dd className="col-span-2 text-jjblue capitalize">{movie.source_type} embed</dd>
              </dl>
              <p className="mt-5 text-xs text-[var(--jj-muted)] leading-relaxed">
                Faith Cinema curates publicly-available embeds from trusted Christian creators. We don't re-host copyrighted media — every title plays from its original creator's channel.
              </p>
            </div>

            {isAdmin && (
              <div className="jj-card p-6">
                <div className="overline text-burgundy">Admin</div>
                <button onClick={remove} className="mt-4 inline-flex items-center gap-2 text-sm text-burgundy hover:text-[var(--jj-burgundy-hover)]" data-testid="cinema-delete">
                  <Trash2 className="h-4 w-4" /> Remove from library
                </button>
              </div>
            )}
          </aside>
        </div>

        {upcomingParties.length > 0 && (
          <section className="mt-12" data-testid="watch-parties-section">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="font-serif text-3xl text-jjblue flex items-center gap-3">
                <CalendarPlus className="h-6 w-6 text-burgundy" strokeWidth={1.4} />
                Upcoming watch parties
              </h2>
              <span className="text-xs text-[var(--jj-muted)]">{upcomingParties.length} scheduled</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {upcomingParties.map((p) => {
                const mine = p.created_by === user?.id;
                const inviteUrl = `${window.location.origin}/api/og/cinema/${movie.id}?party=${p.id}`;
                return (
                  <article key={p.id} className="jj-card p-5 flex flex-col" data-testid={`watch-party-${p.id}`}>
                    <div className="flex items-center gap-2 text-xs text-burgundy">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="font-medium">{formatPartyTime(p.starts_at)}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm text-jjblue">
                      <MapPin className="h-4 w-4 text-[var(--jj-muted)]" />
                      <span>{p.location || "Online"}</span>
                    </div>
                    {p.host?.name && (
                      <div className="mt-2 text-xs text-[var(--jj-muted)] flex items-center gap-2 flex-wrap">
                        <span>
                          Hosted by{" "}
                          {p.created_by ? (
                            <Link to={`/u/${p.created_by}`} className="text-jjblue hover:text-burgundy hover:underline" data-testid={`watch-party-host-link-${p.id}`}>{p.host.name}</Link>
                          ) : p.host.name}
                          {mine && " (you)"}
                        </span>
                        {!mine && p.created_by && (
                          <Link
                            to={`/messages/${p.created_by}`}
                            className="text-burgundy hover:underline inline-flex items-center gap-0.5"
                            data-testid={`watch-party-message-host-${p.id}`}
                          >
                            · Message
                          </Link>
                        )}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <span className="text-xs text-[var(--jj-muted)]">
                        <Users className="h-3.5 w-3.5 inline mr-1" />
                        {p.rsvp_count} going
                      </span>
                      <div className="flex items-center gap-2">
                        <CopyInviteButton
                          url={`${window.location.origin}/api/og/cinema/${movie.id}?party=${p.id}${user?.id ? `&inviter=${user.id}` : ""}`}
                          label="Invite"
                          copiedLabel="Copied ✓"
                          testId={`watch-party-invite-${p.id}`}
                        />
                        <button
                          onClick={() => toggleRsvp(p.id)}
                          className={`text-xs px-4 py-1.5 rounded-full border transition-all ${
                            p.i_rsvp
                              ? "bg-burgundy text-cream border-burgundy"
                              : "bg-cream-surface text-jjblue border-jj hover:border-gold"
                          }`}
                          data-testid={`watch-party-rsvp-${p.id}`}
                        >
                          {p.i_rsvp ? "Going ✓" : "I'm in"}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>

      <WatchPartyDialog
        open={partyOpen}
        movie={movie}
        onClose={() => setPartyOpen(false)}
        onCreated={() => load()}
      />
    </div>
  );
}
